import type { ResponsibleParty, Task } from "./types";

/**
 * P6 XML import — the reverse of `p6xml.ts`'s export, for starting a brand-new planning
 * from an existing Primavera schedule instead of from scratch. Built to tolerate real
 * Primavera Cloud/P6 exports (varying field names, relationship types, activity types),
 * not just files this app produced itself, so it's deliberately permissive: anything it
 * doesn't recognize is skipped with a warning rather than rejected outright.
 *
 * Phase (swimlane) is recovered from an Activity Code Type named "Phase" — the shape
 * `p6xml.ts` exports its own swimlanes as — with each Activity's assigned value read
 * from its `<Code>` children. A file with no such code type (any genuine Primavera
 * export that doesn't happen to have a code type of that exact name) leaves every
 * activity on the caller-supplied default phase instead, same as before.
 *
 * Deliberately out of scope, and left for someone to fill in by hand afterward:
 *  - WBS/scope assignment — P6's WBS hierarchy has no reliable mapping onto this app's
 *    own two-level WBS catalogue, so every imported activity comes in unassigned
 *    (`wbsId: null`), same as any other activity nobody's sorted yet.
 *  - Percent-complete / progress — this app tracks progress as specific calendar days
 *    marked done, which a percentage can't be turned into without also knowing which
 *    days those were, so imported activities always start with none.
 *  - Relationship types other than Finish-to-Start, and any lag — this app's own
 *    scheduling model doesn't have them, so they're dropped (and counted in `warnings`).
 *  - Double-shift team coverage — every imported activity starts as a single team
 *    (`doubleShift: false`), since P6 has no equivalent concept; assign a second team
 *    afterward per activity, in the edit modal, on a project that runs two shifts.
 */

export interface P6ImportResult {
  ok: true;
  tasks: Task[];
  /** Human-readable notes on anything skipped or defaulted, for a toast/summary. */
  warnings: string[];
}
export interface P6ImportError {
  ok: false;
  error: string;
}
export type P6ImportOutcome = P6ImportResult | P6ImportError;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** First matching direct child's text content, trimmed, across a list of tag-name candidates. */
function childText(el: Element, ...tagNames: string[]): string {
  for (const name of tagNames) {
    for (const child of Array.from(el.children)) {
      if (child.tagName === name) return (child.textContent ?? "").trim();
    }
  }
  return "";
}

function parseDate(text: string): Date | null {
  if (!text) return null;
  const d = new Date(text);
  return isNaN(d.getTime()) ? null : d;
}

function parseNumber(text: string): number | null {
  if (!text) return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : null;
}

/** Our own export writes these fields into free-text Notes (see `activityNotes` in
 *  p6xml.ts) — parsed back here so a file this app exported round-trips cleanly. A
 *  genuine Primavera export simply won't have any of these lines, so every field stays
 *  at its default. */
function parseNotes(notes: string): {
  subcontractors: string[];
  responsibleParty: ResponsibleParty;
  requiredEquipment: string;
  requiredPersonnel: string;
  risk: string;
} {
  const result = { subcontractors: [] as string[], responsibleParty: "" as ResponsibleParty, requiredEquipment: "", requiredPersonnel: "", risk: "" };
  notes.split("\n").forEach((line) => {
    const m = /^([^:]+):\s*(.*)$/.exec(line.trim());
    if (!m) return;
    const [, label, value] = m;
    if (label === "Subcontractors") result.subcontractors = value.split(",").map((s) => s.trim()).filter(Boolean);
    else if (label === "Responsible party") result.responsibleParty = value === "RWG" || value === "ABB" ? value : "";
    else if (label === "Required equipment") result.requiredEquipment = value;
    else if (label === "Required personnel") result.requiredPersonnel = value;
    else if (label === "Linked risk") result.risk = value;
  });
  return result;
}

/** A unique, non-empty task id: the activity's own `Id` when present and not already
 *  taken, otherwise a synthesized one — so a file with blank or duplicate activity
 *  codes still imports instead of silently colliding tasks together. */
function uniqueId(raw: string, index: number, seen: Set<string>): string {
  let id = raw || `A${index + 1}`;
  while (seen.has(id)) id = `${id}-${seen.size + 1}`;
  seen.add(id);
  return id;
}

/** Maps each Activity Code *value*'s ObjectId to its CodeValue, for values belonging
 *  to a code type named "Phase" — the reverse of what `p6xml.ts` writes. Empty for a
 *  file with no such code type, which is the case for any genuine Primavera export
 *  that doesn't happen to define one itself. */
function phaseNameByCodeValueObjectId(doc: Document): Map<string, string> {
  const phaseTypeObjectIds = new Set(
    Array.from(doc.getElementsByTagName("ActivityCodeType"))
      .filter((el) => /^phase$/i.test(childText(el, "Name")))
      .map((el) => childText(el, "ObjectId"))
      .filter(Boolean)
  );
  const result = new Map<string, string>();
  if (!phaseTypeObjectIds.size) return result;
  Array.from(doc.getElementsByTagName("ActivityCode")).forEach((el) => {
    if (!phaseTypeObjectIds.has(childText(el, "CodeTypeObjectId"))) return;
    const objectId = childText(el, "ObjectId");
    const value = childText(el, "CodeValue");
    if (objectId && value) result.set(objectId, value);
  });
  return result;
}

/** An activity's assigned phase name, from its own `<Code>` children pointing at a
 *  Phase-type Activity Code value — null when it has none, so the caller can fall
 *  back to its own default. */
function activityPhase(el: Element, phaseNameByValueObjectId: Map<string, string>): string | null {
  for (const child of Array.from(el.children)) {
    if (child.tagName !== "Code") continue;
    const name = phaseNameByValueObjectId.get(childText(child, "ValueObjectId"));
    if (name) return name;
  }
  return null;
}

export function parseP6Xml(xmlText: string, defaultPhase: string): P6ImportOutcome {
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(xmlText, "application/xml");
  } catch {
    return { ok: false, error: "Couldn't parse this file as XML." };
  }
  if (doc.getElementsByTagName("parsererror").length) {
    return { ok: false, error: "Couldn't parse this file as XML — it doesn't look well-formed." };
  }

  const projectEls = Array.from(doc.getElementsByTagName("Project"));
  const activityEls = Array.from(doc.getElementsByTagName("Activity"));
  if (!projectEls.length && !activityEls.length) {
    return { ok: false, error: "No <Project> or <Activity> element found — is this a P6 XML export?" };
  }

  const warnings: string[] = [];
  if (projectEls.length > 1) {
    warnings.push(`This file has ${projectEls.length} projects — only the first one's activities were imported.`);
  }
  const projectObjectId = projectEls[0] ? childText(projectEls[0], "ObjectId") : "";
  // Only filter by project when there's more than one to tell apart, and the activity
  // actually says which project it belongs to — an export with one project sometimes
  // omits ProjectObjectId on its activities entirely, which shouldn't exclude them.
  const relevantActivityEls = projectEls.length > 1
    ? activityEls.filter((el) => {
        const pid = childText(el, "ProjectObjectId");
        return !pid || pid === projectObjectId;
      })
    : activityEls;

  if (!relevantActivityEls.length) {
    return { ok: true, tasks: [], warnings: ["No activities found in this file."] };
  }

  const seenIds = new Set<string>();
  const idByObjectId = new Map<string, string>();

  type Parsed = { objectId: string; id: string; el: Element };
  const parsed: Parsed[] = relevantActivityEls.map((el, i) => {
    const objectId = childText(el, "ObjectId");
    const id = uniqueId(childText(el, "Id"), i, seenIds);
    if (objectId) idByObjectId.set(objectId, id);
    return { objectId, id, el };
  });

  let missingDates = 0;
  let usedFallbackDuration = 0;
  const phaseNameByValueObjectId = phaseNameByCodeValueObjectId(doc);

  const tasksById = new Map<string, Task>();
  parsed.forEach(({ id, el }) => {
    const name = childText(el, "Name") || id;
    const rawType = childText(el, "Type");
    const isMilestone = /milestone/i.test(rawType);

    let durationDays = 0;
    if (!isMilestone) {
      const start = parseDate(childText(el, "PlannedStartDate", "StartDate", "ActualStartDate", "EarlyStartDate"));
      const finish = parseDate(childText(el, "PlannedFinishDate", "FinishDate", "ActualFinishDate", "EarlyFinishDate"));
      if (start && finish) {
        durationDays = Math.max(0, Math.round((finish.getTime() - start.getTime()) / MS_PER_DAY));
      } else {
        const hours = parseNumber(childText(el, "PlannedDuration", "OriginalDuration", "RemainingDuration"));
        if (hours != null) {
          durationDays = Math.max(0, hours / 8);
          usedFallbackDuration++;
        } else {
          durationDays = 1;
          missingDates++;
        }
      }
    }

    const notes = parseNotes(childText(el, "Notes"));
    const phase = activityPhase(el, phaseNameByValueObjectId) ?? defaultPhase;
    const task: Task = {
      id, wbsId: null, name, phase, planDay: "custom",
      baseline: durationDays, o: durationDays, m: durationDays, p: durationDays,
      risk: notes.risk, type: isMilestone ? "milestone" : "activity",
      preds: [], pinnedDay: null, pinnedShift: null, weekendWork: "auto", doubleShift: false,
      subcontractors: notes.subcontractors, responsibleParty: notes.responsibleParty,
      requiredEquipment: notes.requiredEquipment, requiredPersonnel: notes.requiredPersonnel,
      doneDays: [], blocked: false, blockedReason: "", blockedSince: null, requiredStatus: [],
    };
    tasksById.set(id, task);
  });

  if (missingDates) warnings.push(`${missingDates} activit${missingDates === 1 ? "y has" : "ies have"} no usable dates or duration — defaulted to 1 day.`);
  if (usedFallbackDuration) warnings.push(`${usedFallbackDuration} activit${usedFallbackDuration === 1 ? "y" : "ies"} had no start/finish dates — duration was taken from its planned/original/remaining duration instead.`);

  let skippedRelType = 0;
  let skippedUnresolved = 0;
  Array.from(doc.getElementsByTagName("Relationship")).forEach((rel) => {
    const predObjectId = childText(rel, "PredecessorActivityObjectId");
    const succObjectId = childText(rel, "SuccessorActivityObjectId");
    const predId = idByObjectId.get(predObjectId);
    const succId = idByObjectId.get(succObjectId);
    if (!predId || !succId) { skippedUnresolved++; return; }
    const relType = childText(rel, "Type");
    if (relType && !/finish.*to.*start|^FS$/i.test(relType)) { skippedRelType++; return; }
    const succ = tasksById.get(succId);
    if (succ && !succ.preds.includes(predId)) succ.preds.push(predId);
  });
  if (skippedRelType) warnings.push(`${skippedRelType} relationship${skippedRelType === 1 ? "" : "s"} skipped — only Finish-to-Start is supported here.`);
  if (skippedUnresolved) warnings.push(`${skippedUnresolved} relationship${skippedUnresolved === 1 ? "" : "s"} skipped — couldn't resolve one side of the link (probably an activity outside the first project).`);

  return { ok: true, tasks: [...tasksById.values()], warnings };
}
